import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, AlertTriangle } from "lucide-react";
import { migApi, errMsg, isUnavailable } from "./lib";
import { ImportHistory } from "./ImportHistory";
import { MigrationWizard } from "./MigrationWizard";

// Super Admin assisted-migration UI. The import engine lives in NEEC Service; everything here goes through our backend proxy.
export function DataMigrationTab({ client }) {
  const cid = client.id;
  const [types, setTypes] = useState(null);
  const [batches, setBatches] = useState(null);
  const [error, setError] = useState(null);
  const [wizard, setWizard] = useState(null); // null | { batch } | { batch: null }
  const load = async () => {
    setError(null);
    try { const [t, b] = await Promise.all([migApi.types(cid), migApi.list(cid)]); setTypes(t); setBatches(b); }
    catch (e) { setError({ msg: errMsg(e), unavailable: isUnavailable(e), needsLink: e?.response?.data?.detail?.needs_link }); setBatches([]); }
  };
  useEffect(() => { setWizard(null); setBatches(null); setTypes(null); load(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const open = async (b) => {
    try { setWizard({ batch: await migApi.get(cid, b.id) }); } catch (e) { setError({ msg: errMsg(e), unavailable: isUnavailable(e) }); }
  };
  return (
    <div className="space-y-3" data-testid="svc-data-migration">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div><p className="text-sm font-bold tracking-wide">DATA MIGRATION</p><p className="text-[11px] text-muted-foreground">Assisted imports into {client.business_name}'s NEEC Service account. Powered by NEEC Service; nothing is stored in NEEC POS.</p></div>
        <div className="flex gap-1.5"><Button size="sm" variant="ghost" className="h-8" onClick={load} data-testid="mig-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button><Button size="sm" className="h-8" disabled={!types || !!wizard} onClick={() => setWizard({ batch: null })} data-testid="mig-new"><Plus className="w-3.5 h-3.5 mr-1" /> New Migration</Button></div>
      </div>
      {error && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 flex items-start gap-2" data-testid="mig-error">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><div><p className="font-semibold">{error.unavailable ? "NEEC Service migration service unavailable." : error.msg}</p><p>{error.unavailable ? "Try again or refresh." : error.needsLink ? "Link this company to its Service tenant first." : ""}</p><button className="underline mt-1" onClick={load} data-testid="mig-retry">Try again</button></div>
        </div>
      )}
      {wizard && types && <MigrationWizard key={wizard.batch?.id || "new"} cid={cid} types={types} initialBatch={wizard.batch} onClose={() => { setWizard(null); load(); }} onChanged={() => migApi.list(cid).then(setBatches).catch(() => {})} />}
      <div>
        <p className="text-xs font-bold tracking-wide mb-2">IMPORT HISTORY</p>
        {batches === null ? <p className="text-sm text-muted-foreground" data-testid="mig-loading">Loading import history...</p> : <ImportHistory cid={cid} batches={batches} types={types?.record_types} onOpen={open} onChanged={load} />}
      </div>
    </div>
  );
}
